import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/admin/StatusBadge";
import ContactDetailDrawer, { ContactRow } from "@/components/admin/ContactDetailDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Inbox } from "lucide-react";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

const AdminContacts = () => {
  const [rows, setRows] = useState<ContactRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | "new" | "handled">("all");
  const [language, setLanguage] = useState<"all" | "en" | "sv">("all");
  const [selected, setSelected] = useState<ContactRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (!active) return;
      if (!error && data) setRows(data as ContactRow[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (language !== "all" && r.language !== language) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.message.toLowerCase().includes(q) ||
        r.topic.toLowerCase().includes(q) ||
        (r.company ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, status, language]);

  const newCount = useMemo(() => rows.filter((r) => r.status === "new").length, [rows]);

  const handleRowClick = (row: ContactRow) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const handleUpdated = (updated: ContactRow) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
  };

  return (
    <>
      <Helmet>
        <title>Contact submissions · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Contact submissions</h1>
        <p className="text-muted-foreground mt-1">
          {newCount} new · {rows.length} total
        </p>
      </header>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, message…"
            className="pl-9"
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="handled">Handled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={language} onValueChange={(v) => setLanguage(v as typeof language)}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All languages</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="sv">Swedish</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
        {loading ? (
          <table className="w-full text-sm min-w-[720px]">
            <tbody>
              {Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t border-border first:border-t-0">
                  <td className="px-4 py-3"><Skeleton className="h-5 w-14" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-28" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-40" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-8" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : filtered.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm">
            <Inbox className="w-8 h-8 mb-2 opacity-60" />
            No submissions match your filters.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Date</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Topic</th>
                <th className="text-left px-4 py-2 font-medium">Lang</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => handleRowClick(r)}
                  className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <StatusBadge
                      label={r.status === "handled" ? "Handled" : "New"}
                      tone={r.status === "handled" ? "success" : "info"}
                    />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{r.topic}</td>
                  <td className="px-4 py-3 text-xs uppercase text-muted-foreground">{r.language}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      <ContactDetailDrawer
        contact={selected}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onUpdated={handleUpdated}
      />
    </>
  );
};

export default AdminContacts;
